import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsCaretDown } from 'react-icons/bs';

import { type ResponseBoard, type SAQuestion } from '@editor2/types/pageElements';
import ConfigCard from './ConfigCard';

import { validationsBlock } from './styles';
import {
	isExactMatchConfigValidation,
	isExactMatchElementValidation
} from '@editor2/services/elementValidationsService';
import { toggleExactMatchValidationOverride } from '@editor2/services/elementValidationsActionsService';
import { MutableExactMatchValidations } from './MutableExactMatchValidations';
import { ImmutableExactMatchValidations } from './ImmutableExactMatchValidations';
import { NonExactMatchValidation } from './NonExactMatchValidation';
import { type RootState } from '@editor2/reducers/root';

interface Props {
	element: SAQuestion | ResponseBoard;
	courseId: number;
}

// eslint-disable-next-line max-statements
const ValidationsBlock: FC<Props> = (props) => {
	const { element, courseId } = props;

	const [collapsed, setCollapsed] = useState(true);

	const { rejoinders: defaultRejoinders } = useSelector(
		(state: RootState) => state.courses[courseId].defaults?.exactMatchValidation
	);

	const dispatch = useDispatch();

	const exactMatchElementValidations = [],
		exactMatchConfigValidations = [];

	element.validations.forEach((validation) => {
		if (isExactMatchConfigValidation(validation)) {
			exactMatchConfigValidations.push(validation);
		} else if (isExactMatchElementValidation(validation)) {
			exactMatchElementValidations.push(validation);
		}
	});

	const applicableValidations = element.validations.filter((validation) => {
		return element.exactMatchOverride
			? validation.name !== 'exact-match' || validation.source === 'element'
			: validation.source !== 'element';
	});

	const enabledValidationsNumber = applicableValidations
		? applicableValidations.filter((val) => val.enabled).length
		: 0;

	return (
		<div className={validationsBlock}>
			<div className="validationsHeader">
				<div>Validations</div>

				<div className="tools">
					<div>{`${enabledValidationsNumber}/${
						applicableValidations ? applicableValidations.length : 0
					} enabled`}</div>
					<BsCaretDown
						onClick={() => setCollapsed(!collapsed)}
						className={collapsed ? 'collapseIconRotate' : 'collapseIcon'}
					/>
				</div>
			</div>

			{!collapsed && (
				<div className="validationsBody">
					{element.validations.map(
						(validation, index) =>
							validation.name !== 'exact-match' && (
								<NonExactMatchValidation
									element={element}
									validation={validation}
									validationIndex={index}
									courseId={courseId}
									key={index}
								/>
							)
					)}
					<ConfigCard
						enabled={null}
						title="Exact Match Validations"
						override={element.exactMatchOverride}
						setOverride={() =>
							dispatch(toggleExactMatchValidationOverride(element, courseId, defaultRejoinders))
						}>
						{!element.exactMatchOverride &&
							exactMatchConfigValidations.length === 0 &&
							'No exact match validation rules configured'}
						{!element.exactMatchOverride && exactMatchConfigValidations.length > 0 && (
							<ImmutableExactMatchValidations element={element} />
						)}
						{element.exactMatchOverride && (
							<MutableExactMatchValidations element={element} courseId={courseId} />
						)}
					</ConfigCard>
				</div>
			)}
		</div>
	);
};
export default ValidationsBlock;
